import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth } from "./AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const LocationContext = createContext(null);

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) throw new Error("useLocation must be used within a LocationProvider");
  return context;
};

export const LocationProvider = ({ children }) => {
  const { user: authUser } = useAuth();
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(() => {
    return localStorage.getItem("selectedLocationId") || "all";
  });
  const [loading, setLoading] = useState(true);
  // Iter 508f · surface fetch failures LOUDLY. Waleska (PCW) reported
  // "no locations to choose from" — the RCA was that a 401/500 on the
  // list endpoint was silently swallowed, leaving `locations = []` and
  // making the wizard dropdown show only "— No location —". We now
  // track the error explicitly so consumers can render a retry banner.
  //   { code: "network" | "auth" | "http" | "empty", message: string, status?: number }
  const [error, setError] = useState(null);

  const fetchLocations = useCallback(async () => {
    setLoading(true);
    try {
      // Auth rides on the httpOnly session cookie (DEF-23) — forward it with
      // credentials:"include". The legacy localStorage Bearer token no longer
      // exists, so the old header made this 401 and the Add-Client modal
      // falsely showed "No offices set up yet" / "You're signed out".
      const res = await fetch(`${API}/api/admin-setup/locations`, {
        credentials: "include",
      });
      if (!res.ok) {
        // Route 401 to an auth-specific error so the UI can offer a
        // sign-back-in path rather than blaming missing offices.
        if (res.status === 401 || res.status === 403) {
          setError({
            code: "auth",
            status: res.status,
            message: "Your session expired. Sign back in to load the office list.",
          });
        } else {
          let detail = "";
          try { detail = (await res.json())?.detail || ""; } catch { /* ignore */ }
          setError({
            code: "http",
            status: res.status,
            message: `The office list request failed (HTTP ${res.status}${detail ? ` — ${typeof detail === "string" ? detail : JSON.stringify(detail)}` : ""}). Please retry.`,
          });
        }
        console.error("Locations fetch failed:", res.status);
        setLoading(false);
        return;
      }
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setLocations(list);
      setError(list.length === 0 ? { code: "empty", message: "No offices are set up in your tenant. Ask an admin to add one." } : null);

      // Self-heal stale selectedLocationId — if the persisted choice
      // isn't in the available locations (deleted, lost access, or
      // tenant change), reset to "all" so list pages don't silently
      // return 0 rows with no UI to recover.
      if (list.length > 0) {
        const validIds = new Set(list.map((l) => l.id));
        setSelectedLocationId((prev) =>
          prev === "all" || validIds.has(prev) ? prev : "all"
        );
      }
    } catch (e) {
      console.error("Failed to fetch locations:", e);
      setError({
        code: "network",
        message: "Network error loading offices. Check your connection and retry.",
      });
    }
    setLoading(false);
  }, []);

  // Refetch when the authenticated user becomes available (or changes).
  // The provider mounts ABOVE the router, so on the login page there is no
  // token yet and the first fetch short-circuits to a "signed out" error.
  // Without this dependency the office list stayed frozen in that error
  // after an in-tab SPA login (no reload) — which is exactly what blocked
  // the field-visit wizard's Location/Office picker with a false "Session
  // expired" banner. (iter587 · login-race fix)
  useEffect(() => { fetchLocations(); }, [fetchLocations, authUser?.id]);

  useEffect(() => {
    localStorage.setItem("selectedLocationId", selectedLocationId);
  }, [selectedLocationId]);

  const isGlobalAdmin = () => ["super_admin", "admin"].includes(authUser?.role);

  const getUserLocationIds = () => {
    const ids = [];
    if (authUser?.location_id) ids.push(authUser.location_id);
    if (authUser?.secondary_locations) ids.push(...authUser.secondary_locations);
    return ids;
  };

  const effectiveLocationId = () => {
    if (isGlobalAdmin()) {
      return selectedLocationId; // "all" or a specific id
    }
    const userLocs = getUserLocationIds();
    if (userLocs.length === 0) return "all"; // no location assigned, show all
    if (selectedLocationId !== "all" && userLocs.includes(selectedLocationId)) {
      return selectedLocationId;
    }
    return userLocs[0]; // default to primary
  };

  const locationQueryParam = () => {
    const eid = effectiveLocationId();
    return eid === "all" ? "" : eid;
  };

  const getLocationName = (locId) => {
    if (!locId) return "Unassigned";
    const loc = locations.find((l) => l.id === locId);
    return loc?.name || "Unknown";
  };

  return (
    <LocationContext.Provider
      value={{
        locations,
        selectedLocationId,
        setSelectedLocationId,
        effectiveLocationId,
        locationQueryParam,
        isGlobalAdmin,
        getUserLocationIds,
        getLocationName,
        loading,
        error,
        refetchLocations: fetchLocations,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};
